import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  EyeIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { HomeLazySection } from '../HomeLazySection';
import { HomeStaticImage } from '../HomeStaticImage';
import { homeCta } from '../homeCtaStyles';
import { HOME_POPULAR_DROPS } from '../homePopularDrops';

const qualityDrops = HOME_POPULAR_DROPS.slice(0, 3);

const QUALITY_POINTS = [
  {
    icon: SparklesIcon,
    title: 'Premium print, clean detail',
    body: 'Crisp edges. Rich color. Looks intentional—not cheap.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Fit you can trust',
    body: 'Comfortable feel + true-to-size guidance so you don’t guess.',
  },
  {
    icon: CheckCircleIcon,
    title: 'Easy returns (30 days)',
    body: 'If it’s not the vibe, we’ll make it right—fast.',
  },
] as const;

export function HomeQualitySection() {
  return (
    <HomeLazySection
      className="py-14 md:py-20 bg-gradient-to-b from-[#F8FAFC] via-white to-[#fbfaff]"
      placeholderMinHeight="420px"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="relative rounded-[36px] overflow-hidden border border-slate-200 bg-white shadow-sm">
              <HomeStaticImage
                src={qualityDrops[0]?.image ?? ''}
                alt="Style reference — premium print and fabric"
                className="h-[420px] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-slate-900 border border-white/70">
                  Premium cotton
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-slate-900 border border-white/70">
                  Crisp print
                </span>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm h-44">
                <HomeStaticImage
                  src={qualityDrops[1]?.image ?? ''}
                  alt="Preview angle"
                  className="h-44 w-full object-cover"
                />
              </div>
              <div className="rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-sm h-44">
                <HomeStaticImage
                  src={qualityDrops[2]?.image ?? ''}
                  alt="Preview detail"
                  className="h-44 w-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/90 text-slate-900 text-xs font-semibold border border-slate-200 shadow-sm">
              <EyeIcon className="h-4 w-4 text-indigo-700" />
              Quality + fit confidence
            </div>
            <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
              Built for premium print, fit, and finish.
            </h2>
            <p className="mt-3 text-slate-600">
              Live product pages show your exact mockup. These photos are style references for the
              quality bar we aim for on every order.
            </p>

            <div className="mt-7 space-y-3">
              {QUALITY_POINTS.map((x) => {
                const Icon = x.icon;
                return (
                  <div
                    key={x.title}
                    className="rounded-3xl border border-slate-200/80 bg-white/90 p-5 shadow-sm"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100/70 flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-indigo-700" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-extrabold text-slate-900">{x.title}</p>
                        <p className="mt-1 text-sm text-slate-600">{x.body}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-7">
              <Link to="/shop" className={`${homeCta.primary} w-full sm:w-auto`}>
                Shop with confidence
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </HomeLazySection>
  );
}
